import React, { useContext, useRef, useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Icon,
  useResponsiveValue,
} from '@monash/portal-react';
import DraggableWindow from '@/components/ui/draggable-window/DraggableWindow';
import { deepClone } from '@monash/portal-frontend-common';
import SortableDragAndDropWrapper, {
  DND_TYPE,
} from '@/components/ui/drag-and-drop/SortableDragAndDropWrapper';
import { arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { EditShortcutMobileRow } from './edit-shortcut-row/EditShortcutMobileRow';
import { EditShortcutMobileForm } from './edit-shortcut-mobile-form/EditShortcutMobileForm';
import { getShortcut, getShortcutIndex } from '../utils';
import empty from '../assets/empty.svg';
import { WidgetContext } from '@/components/providers/WidgetProvider';
import { MOBILE_RESPONSIVE } from '@/components/ui/main/Main';
import { formatUrl } from '@/utils/format-url';
import c from './edit-shortcuts-mobile.module.scss';
import classNames from 'classnames';

const EditShortcutsMobile = ({
  updateData,
  currentShortcuts,
  escapeFocusRef,
}) => {
  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);
  const isMobile = responsiveSize === 'S';
  const [isConfirmingBackButton, setIsConfirmingBackButton] = useState(false);
  const [inEditShortcuts, setInEditShortcuts] = useState(currentShortcuts);
  const [shortcutsAreValid, setShortcutsAreValid] = useState(true);
  const { editActiveWidget, setEditActiveWidget } = useContext(WidgetContext);
  const scrollContainerRef = useRef();
  const [editingShortcutId, setEditingShortcutId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isListDirty, setIsListDirty] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  // deactivate widget from editing shortcuts in provider
  const deactivateWidget = () => setEditActiveWidget(null);

  // draggable window initial position
  const getTriggerRefPosition = () => {
    if (!escapeFocusRef?.current) return;
    const position = escapeFocusRef.current.getBoundingClientRect();
    return { x: position.right, y: position.bottom };
  };

  // drag and drop
  const [activeId, setActiveId] = useState(null);
  const isDndActivated = activeId !== null;

  // onDragStart
  const activateItem = (e) => {
    setActiveId(e.active.id);
  };

  // onDragCancel
  const deactivateActiveItem = () => {
    setActiveId(null);
  };

  // onDragEnd
  const swapItemOrder = (e) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      setInEditShortcuts((f) => {
        const newShortcuts = deepClone(f);
        return arrayMove(
          newShortcuts,
          getShortcutIndex(active.id, newShortcuts),
          getShortcutIndex(over.id, newShortcuts)
        );
      });
    }
    deactivateActiveItem();
    setIsListDirty(true);
  };

  // validate shortcuts
  const validateShortcuts = (shortcuts) => {
    if (!Array.isArray(shortcuts)) return;

    // empty shortcut list is valid
    if (!shortcuts.length) {
      setShortcutsAreValid(true);
    }

    // find any invalid shortcut
    const invalidShortcut = shortcuts.find(
      (shortcut) => !shortcut.name || !shortcut.url
    );
    setShortcutsAreValid(!invalidShortcut);
  };

  // add new shortcut
  const addNewInEditShortcut = () => {
    editFormActions.open();
  };

  // Actions for screen 1: listing screen
  const listingScreenActions = {
    // back to app
    close: () => {
      deactivateWidget();
      setIsListDirty(false);
      escapeFocusRef.current?.focus();
    },

    confirmBack: (e) => {
      e.preventDefault();
      if (isListDirty) {
        setIsConfirmingBackButton(true);
      } else {
        listingScreenActions.close();
      }
    },
  };

  // Actions for screen 2: edit form
  const editFormActions = {
    open: (shortcutId) => {
      setIsEditing(true);
      setEditingShortcutId(shortcutId);
    },

    // close edit form and return to list
    close: () => {
      setIsConfirmingBackButton(false);
      setIsEditing(false);
      setEditingShortcutId(null);
      setIsFormDirty(false);
    },

    confirmBack: (e) => {
      e.preventDefault();
      if (isFormDirty) {
        setIsConfirmingBackButton(true);
      } else {
        editFormActions.close();
      }
    },
  };

  // save shortcuts
  const saveShortcuts = () => {
    const formattedInEditShortcuts = inEditShortcuts.map((shortcut) => ({
      ...shortcut,
      url: formatUrl(shortcut.url),
    }));
    updateData('shortcuts', formattedInEditShortcuts);
    deactivateWidget();
  };

  const TITLE = (
    <Anchor
      icon={<Icon.ChevronLeft />}
      onClick={
        isEditing
          ? editFormActions.confirmBack
          : listingScreenActions.confirmBack
      }
    >
      Back
    </Anchor>
  );

  const onClose = (event) => {
    if (isEditing) {
      editFormActions.confirmBack(event);
    } else {
      listingScreenActions.confirmBack(event);
    }
  };

  const editingShortcut = editingShortcutId
    ? getShortcut(editingShortcutId, inEditShortcuts)
    : null;

  const shortcutsClasses = classNames(c.shortcuts, {
    [c.disabled]: isConfirmingBackButton,
  });

  return (
    <DraggableWindow
      fullScreen={isMobile}
      title={TITLE}
      isShowing={editActiveWidget}
      focusTrapEnabled={true}
      focusTrapSelectors="button:not([disabled]), input[type=text], input[type=url]"
      keyboardDismissalEnabled={!isDndActivated && !editingShortcutId}
      onClose={onClose}
      initialPosition={getTriggerRefPosition()}
      extraRootProps={{
        role: 'dialog',
        'aria-label': TITLE,
      }}
    >
      <div className={c.editShortcuts}>
        {isEditing ? (
          <EditShortcutMobileForm
            originalShortcut={editingShortcut}
            setInEditShortcuts={setInEditShortcuts}
            isConfirmingBackButton={isConfirmingBackButton}
            closeEditForm={editFormActions.close}
            setIsListDirty={setIsListDirty}
            setIsFormDirty={setIsFormDirty}
            isFormDirty={isFormDirty}
          />
        ) : (
          <>
            <div ref={scrollContainerRef} className={shortcutsClasses}>
              {inEditShortcuts.length ? (
                <>
                  <SortableDragAndDropWrapper
                    sortableItems={inEditShortcuts}
                    onDragStart={activateItem}
                    onDragEnd={swapItemOrder}
                    onDragCancel={deactivateActiveItem}
                    strategy={verticalListSortingStrategy}
                    dndType={DND_TYPE.VERTICAL}
                  >
                    {inEditShortcuts.map((inEditShortcut) => (
                      <EditShortcutMobileRow
                        key={inEditShortcut.id}
                        validateShortcuts={validateShortcuts}
                        isShortcutActive={inEditShortcut.id === activeId}
                        currentShortcut={getShortcut(
                          inEditShortcut.id,
                          currentShortcuts
                        )}
                        setIsListDirty={setIsListDirty}
                        shortcut={inEditShortcut}
                        setInEditShortcuts={setInEditShortcuts}
                        onEdit={editFormActions.open}
                      />
                    ))}
                  </SortableDragAndDropWrapper>
                </>
              ) : (
                <div className={c.empty}>
                  <img src={empty} alt="" />
                  <p>No shortcuts yet</p>
                </div>
              )}
            </div>

            {isConfirmingBackButton ? (
              <div className={c.actions}>
                <Alert type="error">You have made some changes.</Alert>

                <div className={c.buttons}>
                  <Anchor onClick={() => listingScreenActions.close()}>
                    Discard
                  </Anchor>

                  <Button
                    disabled={!shortcutsAreValid}
                    variant="primary"
                    size="small"
                    onClick={saveShortcuts}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className={c.actions}>
                <div className={c.buttons}>
                  <Anchor
                    icon={<Icon.Plus />}
                    onClick={addNewInEditShortcut}
                    onKeyDown={(e) => e.stopPropagation()}
                    mode="card"
                  >
                    Add shortcut
                  </Anchor>

                  <Button
                    disabled={!shortcutsAreValid}
                    variant="primary"
                    size="small"
                    onClick={saveShortcuts}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DraggableWindow>
  );
};

export default EditShortcutsMobile;
